import { userViewerSettingsAtom } from '@/atoms/user.atom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ViewerSettingsDialog } from '@/components/ViewerSettingsDialog';
import { MapLineweightMode } from '@/graphql/graphql';
import { MAP_LINEWEIGHT_MODE_KEY } from '@/lib/mapLineweight';
import { Eye } from '@mynaui/icons-react';
import { useAtomValue } from 'jotai';
import { useFeatureFlagEnabled } from '@/lib/analytics';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ViewerSettingsProps {
  userId: string;
}

interface iLabelProps {
  title: string;
  description: string;
}
const Label: FunctionComponent<iLabelProps> = ({ title, description }) => {
  return (
    <div className='flex flex-col gap-1'>
      <h3 className='font-medium leading-none text-slate-900 dark:text-white'>{title}</h3>
      <p className='text-sm font-medium leading-none text-slate-500 dark:text-white/70'>{description}</p>
    </div>
  );
};

export const ViewerSettings: FunctionComponent<ViewerSettingsProps> = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const isGoogleStreetViewEnabled = useFeatureFlagEnabled('googleStreetview');
  const { t } = useTranslation('account');
  const { t: tProject } = useTranslation('project');

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('viewer.title')}</CardTitle>
          <CardDescription>{t('viewer.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <Label
                title={t('viewer.viewerPreferences')}
                description={t('viewer.viewerPreferencesDescription')}
              />

              <Button variant='outline' onClick={() => setDialogOpen(true)}>
                <Eye className='h-4 w-4 mr-2 stroke-2' />
                {t('viewer.configureSettings')}
              </Button>
            </div>

            {/* Display current settings */}
            {viewerSettings && (
              <div className='mt-4 px-4 py-3 bg-slate-50 dark:bg-stone-900 rounded-sm border border-slate-200 dark:border-stone-800 space-y-2'>
                <h4 className='text-sm font-medium text-slate-700 dark:text-white'>{t('viewer.currentSettings')}</h4>
                <div className='text-xs font-medium text-slate-600 dark:text-white/70 space-y-1'>
                  <div>
                    {t('viewer.pointCloudSize')}: {viewerSettings.pointCloudPointSizeType === 'ADAPTIVE' ? t('viewer.adaptive') : t('viewer.fixed')}
                  </div>
                  {viewerSettings.pointCloudPointSizeType === 'FIXED' && (
                    <div>{t('viewer.pointCloudFixedSize')}: {viewerSettings.pointCloudPointSize}px</div>
                  )}
                  <div>{t('viewer.pointCloudPointBudget')}: {viewerSettings.pointCloudPointBudget.toLocaleString()} {t('viewer.points')}</div>
                  <div>{t('viewer.showLayerSource')}: {viewerSettings.showLayerSource ? t('viewer.enabled') : t('viewer.disabled')}</div>
                  <div>{t('viewer.showLayerUnderType')}: {viewerSettings.showLayerUnderType ? t('viewer.enabled') : t('viewer.disabled')}</div>
                  <div>{t('viewer.showImagePOIs')}: {viewerSettings.showImagePOIs ? t('viewer.enabled') : t('viewer.disabled')}</div>
                  <div>
                    {t('viewer.showPointCloudBoundingBoxes')}:{' '}
                    {viewerSettings.showPointCloudBoundingBoxes ? t('viewer.enabled') : t('viewer.disabled')}
                  </div>
                  <div>
                    {t('viewer.showOrthoGrid')}:{' '}
                    {viewerSettings.showOrthoGrid ? t('viewer.enabled') : t('viewer.disabled')}
                  </div>
                  <div>
                    {tProject('map.lineweight.title')}:{' '}
                    {tProject(`map.lineweight.${MAP_LINEWEIGHT_MODE_KEY[viewerSettings.mapLineweightMode ?? MapLineweightMode.Screen]}`)}
                  </div>
                  {isGoogleStreetViewEnabled && (
                    <div className='flex items-center gap-1'>
                      {t('viewer.googleStreetViewPoiColor')}:{' '}
                      <span
                        className='inline-block size-3 rounded-full'
                        style={{ backgroundColor: viewerSettings.googleStreetViewPoiColor || 'hotpink' }}
                      />
                      {viewerSettings.googleStreetViewPoiColor || 'hotpink'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {dialogOpen && <ViewerSettingsDialog open={dialogOpen} onOpenChange={setDialogOpen} />}
    </>
  );
};
